<template>
    <AddFileSection
        :id="AddFileFormSectionIds.DOCUMENTS"
        :sectionDescription="
            hasDocuments
                ? 'Add or remove documents to include in this file. Select clients and toggle on eSign for signatures.'
                : ''
        "
        :sectionHeading="hasDocuments ? 'Send documents' : ''"
    >
        <SendDocumentsCardButton
            v-if="!addFileStore.branch?.id"
            disabled
        />
        <div
            v-else
            :class="{ 'mt-2': hasDocuments }"
        >
            <MultiFileDocumentUpload
                active
                :allowTemplateSelection="false"
                :branchId="addFileStore.branch.id"
                :clients="clientOptions"
                :dsUploadSize="hasDocuments ? 'md' : 'collapsed'"
                :enableNotificationFlag="false"
                headerText="Added documents"
                invertedForm
                :routes="formRoutes"
                @addClient="scrollToClientsSection"
            >
                <template
                    v-if="!hasDocuments"
                    #clickTrigger="{ triggerFilePicker }"
                >
                    <SendDocumentsCardButton @addDocuments="triggerFilePicker" />
                </template>
            </MultiFileDocumentUpload>

            <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission-->
            <DocumentUploadHiddenFormFields
                :formData
                :omitProperties="['notify', '_token', '_method', 'branch_id']"
            />
            <!-- Produces a list of recipients that are caught by `addViewFile.blade#getSelectedRecipientsIds` via class selector
               before submission
               This prevents clients that are selected for document tasks from being dropped if not assigned to other tasks too
               [CLENG-1942] - Cleanup this too
            -->
            <select
                v-for="doc in fileDocumentStore.documentConfigs.values()"
                :key="doc.documentId"
                class="document-recipients-select hidden"
                multiple
            >
                <option
                    v-for="recipient in doc.recipients"
                    :key="recipient"
                    selected
                    :value="recipient"
                ></option>
            </select>
            <!--
            CLENG-1942: cleanup this alongside hidden inputs. It builds a summary of attached documents 
            for the confirmation modal
            -->
            <div
                id="add-file-document-summary"
                class="hidden"
            >
                <div
                    v-for="doc in documentUploadSummary"
                    :key="doc.id"
                >
                    <input
                        checked
                        class="document-checkbox hidden"
                        type="checkbox"
                    />
                    <label style="display: block">{{ doc.title }}</label>
                    <div
                        v-for="recipient in doc.recipients"
                        :key="recipient.name"
                        style="display: block"
                    >
                        <span class="capitalize">{{ recipient.client?.role }}:</span>
                        <b>{{ recipient.name }}</b>
                        <span
                            v-if="recipient.isSigner"
                            class="text-ds-red-500"
                        >
                            (signer)
                        </span>
                    </div>
                    <div v-if="doc.recipients.length === 0">
                        <span class="text-ds-red-500">No recipients</span>
                    </div>
                </div>
            </div>
        </div>

        <DsDialog
            v-model:open="isEsignInvalidationDialogOpen"
            primaryAction="Update eSignature documents"
            secondaryAction="Close"
            title="eSignature document update is required"
            @clickPrimary="handleInvalidationDialogConfirm"
            @clickSecondary="isEsignInvalidationDialogOpen = false"
        >
            <p class="text-content-lg">
                Updating this client's information requires you to redo the eSignature flow. The affected documents have been
                highlighted so you can quickly restart them.
            </p>
        </DsDialog>
    </AddFileSection>
</template>

<script setup lang="ts">
import { fileSubmissionErrors } from '@/company-portal/add-file';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type { TPSDocument, TPSOrderInfo } from '@/company-portal/add-file/add-file.types';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { availableClients } from '@/company-portal/add-file/available-clients';
import { useAddFileFormScroller } from '@/company-portal/add-file/use-add-file-form-scroller';
import DsDialog from '@/components/design-system/DsDialog.vue';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import DocumentUploadHiddenFormFields from '@/components/Views/Company/AddFile/DocumentUploadHiddenFormFields.vue';
import SendDocumentsCardButton from '@/components/Views/Company/AddFile/SendDocumentsCardButton.vue';
import type { ClientOptionInput } from '@/components/Views/Company/File/Documents/document-upload-utils';
import { mapDocumentErrors } from '@/components/Views/Company/File/Documents/document-upload-utils';
import MultiFileDocumentUpload from '@/components/Views/Company/File/Documents/MultiFileDocumentUpload.vue';
import type { AddFileFormDocumentRoutes, TPSDocumentVendorContext } from '@/components/Views/Company/File/file.types';
import { type DocumentUploadErrorResponse } from '@/components/Views/Company/File/file.types';
import { ESIGN_INVALIDATION_DIALOG_INJECTION_KEY } from '@/injection-keys';
import { scrollToElement } from '@/shared/utils/browser';
import { joinFirstLast } from '@/shared/utils/strings';
import { useFileDocumentStore } from '@/stores/file-document.store';
import { computed, onMounted, provide, ref, watch } from 'vue';

const props = withDefaults(
    defineProps<{
        clientSectionId?: string;
        routes: {
            esign_edit_url: string; // i.e.: /user/home/document/eSign/edit
            esign_edit_url_vendor: string; // i.e.: /user/vendor/order/esign/edit
        };
    }>(),
    {
        clientSectionId: 'clients-section',
    },
);

const fileDocumentStore = useFileDocumentStore();
const addFileStore = useAddFileStore();

const { scrollToDocumentSection } = useAddFileFormScroller();

// Shared, section-level dialog raised when editing a client invalidates a document's prepared
// eSignature. Each affected document card opens it via the provided handler below.
const isEsignInvalidationDialogOpen = ref(false);
provide(ESIGN_INVALIDATION_DIALOG_INJECTION_KEY, {
    openEsignInvalidationDialog: () => (isEsignInvalidationDialogOpen.value = true),
    closeEsignInvalidationDialog: () => (isEsignInvalidationDialogOpen.value = false),
});

const handleInvalidationDialogConfirm = () => {
    isEsignInvalidationDialogOpen.value = false;
    scrollToDocumentSection();
};

const formRoutes = computed<AddFileFormDocumentRoutes>(() => {
    return {
        esign_edit: addFileStore.tpsOrder ? props.routes.esign_edit_url_vendor : props.routes.esign_edit_url,
    };
});

const clientOptions = computed<ClientOptionInput[]>(() =>
    availableClients.new.map(client => ({
        id: client.id,
        value: client.id,
        email: client.email,
        first: client.first,
        last: client.last,
        role: client.role as ClientOptionInput['role'], // assertion: the role types are incompatible between this and the file details client type
    })),
);
const hasDocuments = computed(() => fileDocumentStore.documentConfigs.size > 0);
const formData = computed(() => fileDocumentStore.getFormUploadFormDataV1());

const documentUploadSummary = computed(() => {
    return Array.from(fileDocumentStore.documentConfigs.values()).map(config => {
        return {
            id: config.documentId,
            title: config.title,
            recipients: config.recipients.map(recipientId => {
                const client = clientOptions.value.find(c => String(c.id) === String(recipientId));
                const name = client ? joinFirstLast(client.first, client.last) : 'Unknown Recipient';
                const isSigner = config.signers.includes(recipientId);
                return {
                    client,
                    isSigner,
                    name,
                };
            }),
        };
    });
});

const scrollToClientsSection = () => {
    requestAnimationFrame(() => {
        scrollToElement(document.getElementById(props.clientSectionId));
    });
};

watch(
    () => fileSubmissionErrors.validationErrors,
    errors => {
        if (!errors) return;
        // assertion: the add-file form's ServerErrorResponse and DocumentUploadErrorResponse
        // share the same structural shape (`{ errors?, message? }`) at this boundary
        const documentErrors = errors as DocumentUploadErrorResponse;
        Array.from(fileDocumentStore.documentConfigs.values()).forEach(config => {
            fileDocumentStore.updateEsignStateInvalidationError(config.documentId, '');
            fileDocumentStore.updateDocumentErrors(config.documentId, mapDocumentErrors(documentErrors, config.formKey));
        });
    },
);

const ingestTpsOrderDocuments = (tpsOrder: TPSOrderInfo) => {
    const vendorContext: TPSDocumentVendorContext = {
        vendor_order_id: tpsOrder.vendor_order_id,
        vendor_transaction_id: tpsOrder.vendor_transaction_id,
        vendor: tpsOrder.vendor,
    };
    tpsOrder.documents?.forEach((tpsDocument: TPSDocument) => {
        fileDocumentStore.addDocument({
            documentId: `${tpsDocument.id}`,
            tpsDocument: {
                ...tpsDocument,
                ...vendorContext,
            },
        });
    });
};

onMounted(() => {
    if (addFileStore.tpsOrder) {
        ingestTpsOrderDocuments(addFileStore.tpsOrder);
    }
});
</script>
